// Design System / Theme
// Centralized colors, typography, spacing, and component styles

// Light mode colors
export const lightColors = {
  // Primary Brand
  primary: '#7FB069',      // Sage green from mockup
  primaryDark: '#5C8A4D',
  primaryLight: '#9DC183',
  
  // Neutral
  background: '#F8F9FA',
  backgroundDark: '#E9ECEF',
  surface: '#FFFFFF',
  overlay: 'rgba(0, 0, 0, 0.5)',
  
  // Text
  text: '#212529',
  textSecondary: '#6C757D',
  textLight: '#ADB5BD',
  textInverse: '#FFFFFF',
  
  // Status
  success: '#28A745',
  warning: '#FFC107',
  error: '#DC3545',
  info: '#17A2B8',
  
  // Borders
  border: '#DEE2E6',
  borderLight: '#E9ECEF',
  
  // Map colors
  hotspot: '#FF6B6B',
  club: '#4ECDC4',
  player: '#7FB069',
  
  // Navigation
  tabBarBackground: '#FFFFFF',
  tabBarBorder: '#E9ECEF',
  tabBarInactive: '#6C757D',
};

// Dark mode colors
export const darkColors = {
  // Primary Brand (keep the same accent)
  primary: '#7FB069',
  primaryDark: '#5C8A4D',
  primaryLight: '#9DC183',
  
  // Neutral - Dark greys
  background: '#1A1A1A',       // Main dark grey background
  backgroundDark: '#121212',   // Even darker for depth
  surface: '#2D2D2D',          // Cards, modals, etc
  overlay: 'rgba(0, 0, 0, 0.7)',
  
  // Text - Light colors for dark mode
  text: '#F5F5F5',
  textSecondary: '#B0B0B0',
  textLight: '#808080',
  textInverse: '#1A1A1A',
  
  // Status (slightly adjusted for dark mode visibility)
  success: '#4CAF50',
  warning: '#FFB300',
  error: '#EF5350',
  info: '#29B6F6',
  
  // Borders
  border: '#404040',
  borderLight: '#333333',
  
  // Map colors (same as light)
  hotspot: '#FF6B6B',
  club: '#4ECDC4',
  player: '#7FB069',
  
  // Navigation
  tabBarBackground: '#1A1A1A',
  tabBarBorder: '#333333',
  tabBarInactive: '#FFFFFF',  // White icons by default in dark mode
};

// Legacy export for backward compatibility
export const colors = lightColors;

// Helper function to get colors based on theme
export const getColors = (isDarkMode) => isDarkMode ? darkColors : lightColors;

export const typography = {
  // Font sizes
  h1: 32,
  h2: 28,
  h3: 24,
  h4: 20,
  h5: 18,
  h6: 16,
  body: 16,
  bodySmall: 14,
  caption: 12,
  
  // Font weights
  regular: '400',
  medium: '500',
  semiBold: '600',
  bold: '700',
  
  // Line heights
  lineHeightTight: 1.2,
  lineHeightNormal: 1.5,
  lineHeightRelaxed: 1.75,
};

export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
};

export const borderRadius = {
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  pill: 999,
};

export const shadows = {
  small: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  medium: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  large: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 6,
  },
};

// Dynamic shadows for dark mode
export const getShadows = (isDarkMode) => isDarkMode ? {
  small: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.3,
    shadowRadius: 2,
    elevation: 1,
  },
  medium: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.4,
    shadowRadius: 4,
    elevation: 3,
  },
  large: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.5,
    shadowRadius: 8,
    elevation: 6,
  },
} : shadows;

// Reusable component styles (using light mode by default)
export const components = {
  // Button variants
  button: {
    primary: {
      backgroundColor: colors.primary,
      borderRadius: borderRadius.md,
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.lg,
      alignItems: 'center',
      justifyContent: 'center',
      ...shadows.small,
    },
    secondary: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.md,
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.lg,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: colors.border,
      ...shadows.small,
    },
    outlined: {
      backgroundColor: 'transparent',
      borderRadius: borderRadius.md,
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.lg,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 2,
      borderColor: colors.primary,
    },
    text: {
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.md,
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  
  buttonText: {
    primary: {
      color: colors.textInverse,
      fontSize: typography.body,
      fontWeight: typography.semiBold,
    },
    secondary: {
      color: colors.text,
      fontSize: typography.body,
      fontWeight: typography.semiBold,
    },
    outlined: {
      color: colors.primary,
      fontSize: typography.body,
      fontWeight: typography.semiBold,
    },
    text: {
      color: colors.primary,
      fontSize: typography.body,
      fontWeight: typography.medium,
    },
  },
  
  // Card
  card: {
    backgroundColor: colors.surface,
    borderRadius: borderRadius.lg,
    padding: spacing.md,
    ...shadows.medium,
  },
  
  // Input (transparent, subtle bottom border)
  input: {
    backgroundColor: 'transparent',
    borderRadius: borderRadius.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    fontSize: typography.body,
    color: colors.text,
    borderWidth: 0,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  
  // Badge
  badge: {
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: borderRadius.pill,
    alignSelf: 'flex-start',
  },
};

// Dynamic component styles based on theme
export const getComponents = (isDarkMode) => {
  const themeColors = getColors(isDarkMode);
  const themeShadows = getShadows(isDarkMode);
  
  return {
    button: {
      primary: {
        backgroundColor: themeColors.primary,
        borderRadius: borderRadius.md,
        paddingVertical: spacing.md,
        paddingHorizontal: spacing.lg,
        alignItems: 'center',
        justifyContent: 'center',
        ...themeShadows.small,
      },
      secondary: {
        backgroundColor: themeColors.surface,
        borderRadius: borderRadius.md,
        paddingVertical: spacing.md,
        paddingHorizontal: spacing.lg,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderColor: themeColors.border,
        ...themeShadows.small,
      },
      outlined: {
        backgroundColor: 'transparent',
        borderRadius: borderRadius.md,
        paddingVertical: spacing.md,
        paddingHorizontal: spacing.lg,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 2,
        borderColor: themeColors.primary,
      },
      text: {
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.md,
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
    buttonText: {
      primary: {
        color: themeColors.textInverse,
        fontSize: typography.body,
        fontWeight: typography.semiBold,
      },
      secondary: {
        color: themeColors.text,
        fontSize: typography.body,
        fontWeight: typography.semiBold,
      },
      outlined: {
        color: themeColors.primary,
        fontSize: typography.body,
        fontWeight: typography.semiBold,
      },
      text: {
        color: themeColors.primary,
        fontSize: typography.body,
        fontWeight: typography.medium,
      },
    },
    card: {
      backgroundColor: themeColors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      ...themeShadows.medium,
    },
    input: {
      backgroundColor: 'transparent',
      borderRadius: borderRadius.md,
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.md,
      fontSize: typography.body,
      color: themeColors.text,
      borderWidth: 0,
      borderBottomWidth: 1,
      borderBottomColor: themeColors.border,
    },
    badge: {
      paddingVertical: spacing.xs,
      paddingHorizontal: spacing.sm,
      borderRadius: borderRadius.pill,
      alignSelf: 'flex-start',
    },
  };
};

export default {
  colors,
  lightColors,
  darkColors,
  getColors,
  typography,
  spacing,
  borderRadius,
  shadows,
  getShadows,
  components,
  getComponents,
};
